import { getNodeInstanceWaitEventsWire } from '../endpoints/getNodeInstanceWaitEventsWire';
import { acceptDegraded } from 'validation';

/** Which mechanism declared the wait: the node's own definition, or a flow node. */
export type WaitEventSource = 'node' | 'flow';

/** A single event a node instance is waiting on (UI-facing, camelCase). */
export interface WaitEvent {
  readonly source: WaitEventSource;
  readonly eventId: number | null;
  readonly eventName: string;
  readonly eventType: string | null;
  readonly zoneName: string;
  readonly autoSubmit: boolean;
  readonly autoClear: boolean;
  readonly precondition: string | null;
  readonly raised: boolean;
}

// The three flags are nullable on the node wire (nullable columns, selected raw) and the UI needs booleans. Null collapses
// to false, which is what nap-backend's own NodeInstanceBlockerService does with the same columns — it normalises them as
// `row.auto_submit === true` before responding, so the two endpoints agree on what an unset flag means. For `raised` that
// reads correctly too: `is_satisfied` null means the wait has not been evaluated, and an unevaluated wait is not satisfied.

/**
 * The events a node instance is waiting on, flattening the node and flow wire sources into one list
 * tagged by `source` so the UI can distinguish otherwise-identical rows that point at the same event
 * definition. Returns an empty array when the instance has none.
 */
export const getNodeInstanceWaitEvents = async (nodeInstanceId: number | string): Promise<readonly WaitEvent[]> => {
  const { node_wait_events, flow_wait_events } = acceptDegraded(await getNodeInstanceWaitEventsWire(nodeInstanceId));
  return [
    ...node_wait_events.map((w) => ({
      source: 'node' as const,
      eventId: w.event_id,
      eventName: w.event_name,
      eventType: w.event_type,
      zoneName: w.zone_name,
      autoSubmit: w.auto_submit ?? false,
      autoClear: w.auto_clear ?? false,
      precondition: w.precondition,
      raised: w.raised ?? false,
    })),
    ...flow_wait_events.map((w) => ({
      source: 'flow' as const,
      eventId: w.event_id,
      eventName: w.event_name,
      eventType: w.event_type,
      zoneName: w.zone_name,
      autoSubmit: w.auto_submit,
      autoClear: w.auto_clear,
      precondition: w.precondition,
      raised: w.raised,
    })),
  ];
};
